import React, { useEffect, useRef, useCallback, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import stringify from '../utils/stableStringify';

import { Template } from 'devextreme-react/core/template';
import Toolbar from 'devextreme-react/toolbar';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { Button } from 'devextreme-react/button';
import AntLocalization from '../utils/AntLocalization';

import AntCondition from '../utils/AntCondition';
import AntConfig from '../utils/AntConfig';
import { AntGlobal } from '../utils/AntContext';
import AntMessageBox from './AntPOSMessageBox';
import { AwesomeButton } from 'react-awesome-button';
import 'react-awesome-button/dist/styles.css';
import './AntPOSVoucherActions.scss';

const ACTION_LABELS = {
    actionRelease: { vi: 'Xác nhận', en: 'Release', fr: 'Libérer', es: 'Liberar', id: 'Rilis', ja: 'リリース', zh: '释放', ko: '해제', hi: 'रिलीज़', ar: 'إصدار' },
    actionPost: { vi: 'Ghi nhận', en: 'Post', fr: 'Publier', es: 'Publicar', id: 'Posting', ja: '投稿', zh: '发布', ko: '게시', hi: 'पोस्ट', ar: 'نشر' },
    actionCancel: { vi: 'Hủy phiếu', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', id: 'Batal', ja: 'キャンセル', zh: '取消', ko: '취소', hi: 'रद्द करें', ar: 'إلغاء' },
    actionApprove: { vi: 'Phê duyệt', en: 'Approve', fr: 'Approuver', es: 'Aprobar', id: 'Setuju', ja: '承認', zh: '批准', ko: '승인', hi: 'स्वीकृत', ar: 'موافقة' },
    actionReject: { vi: 'Từ chối', en: 'Reject', fr: 'Rejeter', es: 'Rechazar', id: 'Tolak', ja: '却下', zh: '拒绝', ko: '거부', hi: 'अस्वीkar', ar: 'رفض' },
    actionClose: { vi: 'Đóng', en: 'Close', fr: 'Fermer', es: 'Cerrar', id: 'Tutup', ja: '閉じる', zh: '关闭', ko: '닫기', hi: 'बंद करें', ar: 'إغلاق' }
};

export default forwardRef(function (props, ref) {
    const navigate = useNavigate();
    const appGlobal = AntGlobal();

    const mainTable = props.table || props.API?.entityName || '';
    const mainConfig = useMemo(() => {
        if (!mainTable) return null;
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === mainTable);
        if (found) return found.config;

        const cnf = new AntConfig(appGlobal, mainTable);
        if (!Array.isArray(appGlobal.tableConfigs)) appGlobal.tableConfigs = [];
        appGlobal.tableConfigs.push({ tableName: mainTable, config: cnf });
        return cnf;
    }, [mainTable, appGlobal]);

    const [mainItems, setMainItems] = useState([]);
    const [processingName, setProcessingName] = useState(props.processingButton);
    const isDisabled = useMemo(() => processingName && mainItems.find((i) => i.waiting && i.name === processingName), [processingName, mainItems]);
    const msgBox = useRef();
    const [shouldRender, setShouldRender] = useState(1);

    const forceRender = useCallback(() => setShouldRender((v) => v + 1), []);

    const _initMainItems = useCallback(() => {
        const eName = props.actionPack || props.table || props.API?.entityName;
        let defaultActions = mainConfig?.getCnfVoucherActions(eName) || [];

        const isVisible = (target, cond) => cond?.visible !== undefined ? AntCondition.isVisible(target, JSON.parse(stringify(cond))) : true;

        if (Array.isArray(props.actions)) {
            defaultActions = defaultActions.filter(item => {
                const found = props.actions.find(a => a.name === item.name);
                return found ? isVisible(props.parentData, found) : false;
            });
        }

        let ar = [];
        let index = 1;

        if (props.showActions) {
            const cnfActions = [...defaultActions];
            if (Array.isArray(props.items)) cnfActions.push(...props.items);

            if (Array.isArray(props.parentData?.actions)) {
                const standardActions = [
                    { name: 'actionRelease', icon: 'parentfolder', defaultText: ACTION_LABELS.actionRelease },
                    { name: 'actionPost', icon: props.actionPost?.icon || 'upload', defaultText: props.actionPost?.text || ACTION_LABELS.actionPost },
                    { name: 'actionCancel', icon: 'trash', defaultText: props.actionCancel?.text || ACTION_LABELS.actionCancel },
                    { name: 'actionApprove', icon: 'check', defaultText: ACTION_LABELS.actionApprove },
                    { name: 'actionReject', icon: 'clear', defaultText: ACTION_LABELS.actionReject }
                ];

                standardActions.forEach(sa => {
                    const action = props.parentData.actions.find(a => a.visible && a.name === sa.name);
                    if (action) {
                        const found = cnfActions.find(a => a.name === sa.name);
                        if (isVisible(props.parentData, found)) {
                            if (!ar.some(a => a.name === sa.name)) {
                                ar.push({
                                    icon: sa.icon,
                                    text: AntLocalization.translateText(sa.defaultText),
                                    name: sa.name,
                                    waiting: true,
                                    widget: 'Button',
                                    index: index++
                                });
                            }
                        }
                    }
                });
            }

            cnfActions.forEach(item => {
                let strText = Array.isArray(item.text) ? AntCondition.getValue(props.parentData, item.text) : item.text;
                item.text = AntLocalization.translateText(strText);

                const visible = props.parentData ? isVisible(props.parentData, item) : true;
                if (visible && (!item.name || !['actionClose'].includes(item.name))) {
                    if (!ar.some(a => a.name === item.name)) ar.push({ ...item, index: index++ });
                }
            });
        }

        if (Array.isArray(props.parentData?.actions)) {
            props.parentData.actions.forEach(action => {
                if (action.visible && ['actionClose', 'actionCancel'].includes(action.name)) {
                    const found = defaultActions.find(a => a.name === action.name);
                    const found2 = Array.isArray(props.items) ? props.items.find(a => a.name === action.name) : {};

                    if (isVisible(props.parentData, found)) {
                        if (!ar.some(a => a.name === action.name)) {
                            const isClose = action.name === 'actionClose';
                            ar.push({
                                ...found, ...found2,
                                icon: isClose ? 'check' : 'trash',
                                name: action.name,
                                waiting: true,
                                text: AntLocalization.translateText(isClose ? (props.actionClose?.text || ACTION_LABELS.actionClose) : (props.actionCancel?.text || ACTION_LABELS.actionCancel)),
                                index: isClose ? index + 1 : index + 3,
                                widget: 'Button',
                                showText: isClose ? undefined : 'inMenu'
                            });
                        }
                    }
                }
            });
        }

        if (Array.isArray(props.items)) {
            ar = ar.filter(item => {
                const found = props.items.find(a => a.name === item.name);
                return found ? isVisible(props.parentData, found) : true;
            });
        }

        ar.sort((a, b) => a.index - b.index);

        if (Array.isArray(props.parentData?.actions)) {
            ar = ar.filter(item => {
                const found = props.parentData.actions.find(a => a.name === item.name);
                return (found?.visible) ? isVisible(props.parentData, found) : false;
            });
        }

        if (Array.isArray(props.actions)) {
            ar = ar.filter(item => {
                const found = props.actions.find(a => a.name === item.name);
                return found ? isVisible(props.parentData, found) : false;
            }).map(a => {
                a.text = AntLocalization.translateText(a.text);
                const found = props.actions.find(i => i.name === a.name);
                if (found) {
                    a.sortIndex = found.sortIndex;
                    a.classNames = found.classNames;
                }
                return a;
            });
            ar.sort((a, b) => (b.sortIndex || 0) - (a.sortIndex || 0));
        }

        return ar;
    }, [mainConfig, props.showActions, props.actionPack, props.table, props.API, props.items, props.parentData, props.actions, props.actionPost, props.actionCancel, props.actionRelease, props.actionClose]);

    const showAlert = useCallback((args, onClosedCallback) => msgBox.current?.show(args, onClosedCallback), []);

    const onActionConfirmed = useCallback(async (btn) => {
        if (!btn?.name) return;
        const mapping = {
            actionCancel: props.onActionCancel,
            actionRelease: props.onActionRelease,
            actionClose: props.onActionClose,
            actionPost: props.onActionPost,
            actionApprove: props.onActionApprove,
            actionReject: props.onActionReject,
            actionPrint: props.onPrintClick
        };
        const handler = mapping[btn.name];
        if (handler) return await handler(btn);
        return await props.onAction?.(btn);
    }, [props.onActionCancel, props.onActionRelease, props.onActionClose, props.onActionPost, props.onActionApprove, props.onActionReject, props.onPrintClick, props.onAction]);

    const onAction = useCallback(async (btn, item) => {
        if (!btn?.name) return;
        if (item) {
            const confirm = item.confirm;
            if (confirm) {
                const content = AntLocalization.translateText(confirm.message || { vi: 'Bạn có muốn thực hiện không ?', en: 'Are you sure ?', fr: 'Êtes-vous sûr ?', es: '¿Está seguro?', id: 'Apakah Anda yakin?', ja: 'よろしいですか？', zh: '您确定吗？', ko: '확실합니까?', hi: 'क्या आप सुनिश्चित हैं?', ar: 'هل أنت متأكد؟' });
                const title = AntLocalization.translateText(confirm.title || { vi: 'Thông báo', en: 'Warning', es: 'Advertencia', id: 'Peringatan', ja: '警告', zh: '警告', ko: '경고', hi: 'चेतावनी', ar: 'تحذير', fr: 'Attention' });
                const btnText = AntLocalization.translateText(confirm.btnText || { vi: 'Có', en: 'Yes', fr: 'Oui', es: 'Sí', id: 'Ya', ja: 'はい', zh: '是', ko: '예', hi: 'हां', ar: 'نعم' });
                const cancelText = AntLocalization.translateText({ vi: 'Không', en: 'No', fr: 'Non', es: 'No', id: 'Tidak', ja: 'いいえ', zh: '否', ko: '아니오', hi: 'नहीं', ar: 'لا' });

                showAlert({
                    title, content,
                    buttons: [{ width: confirm.btnWidth || 150, icon: 'check', text: btnText, name: 'btnYes' }],
                    cancelText
                }, async (btnResult) => {
                    if (btnResult.name === 'btnYes') await onActionConfirmed({ ...btn, ...item });
                });
            } else {
                await onActionConfirmed({ ...btn, ...item });
            }
        }
    }, [onActionConfirmed, showAlert]);

    const initMainItems = useCallback(() => {
        let title = props.title;
        if (props.upperCase) title = String(title).toUpperCase();
        title = AntLocalization.translateText(title);

        let items = [];
        if (props.renderParent !== false) {
            items = _initMainItems().map(item => {
                const wType = item.widget === 'CheckBox' ? 'dxCheckBox' : item.widget === 'Button' ? 'dxButton' : (item.widget || 'dxButton');
                return {
                    name: item.name,
                    classNames: item.classNames,
                    location: item.location || 'center',
                    locateInMenu: item.locateInMenu || 'auto',
                    widget: wType,
                    waiting: Boolean(item.waiting),
                    showText: item.showText !== undefined ? (item.showText === false ? 'inMenu' : item.showText) : 'always',
                    options: {
                        ...item.options,
                        items: item.items,
                        valueExpr: item.valueExpr,
                        displayExpr: item.displayExpr,
                        icon: item.icon,
                        text: item.label ? AntLocalization.translateText(item.label) : AntLocalization.translateText(item.text),
                        value: item.name,
                        width: item.width,
                        stylingMode: item.stylingMode || 'text',
                        onClick: async (e, index) => {
                            if (item.name) {
                                if (item.onClick) await item.onClick({ name: item.name, value: item.value, text: item.text, index });
                                else await onAction({ name: item.name, value: item.value, text: item.text, index }, item);
                            }
                        },
                        onItemClick: async (e, index) => {
                            if (e.itemData?.name) {
                                if (item.onClick) await item.onClick({ name: e.itemData.name, value: e.itemData.value, text: e.itemData.text, index });
                                else await onAction({ name: e.itemData.name, value: e.itemData.value, text: e.itemData.text, index }, item);
                            }
                        }
                    }
                };
            });
        }

        return items.filter(Boolean).map(item => ({
            ...item,
            disabled: Boolean(processingName && item.name !== processingName),
            template: (item.waiting || item.classNames) ? 'renderButton' : undefined
        }));
    }, [processingName, onAction, _initMainItems, props.renderParent, props.title, props.upperCase]);

    const setActionProgress = useCallback((name, show, cb) => {
        if (processingName !== name) {
            const item = mainItems.find(f => f.name === name);
            if (item) {
                setProcessingName(show ? name : null);
                cb?.(item);
            }
        }
    }, [processingName, mainItems]);

    const onButtonClick = useCallback((btnName) => {
        const item = mainItems.find(i => i.name === btnName);
        if (item) {
            props.onActionProcessing?.(item);
            setActionProgress(item.name, true, async (targetItem) => {
                await targetItem.options?.onClick?.(targetItem, mainItems.indexOf(targetItem));
                setActionProgress(targetItem.name, false);
                props.onActionProceeed?.(targetItem);
            });
        }
    }, [props.onActionProcessing, props.onActionProceeed, setActionProgress, mainItems]);

    useImperativeHandle(ref, () => ({
        refreshActions: () => {
            setMainItems(initMainItems());
            forceRender();
        }
    }));

    useEffect(() => {
        setMainItems(initMainItems());
        forceRender();
    }, [initMainItems, forceRender]);

    useEffect(() => setProcessingName(props.processingButton), [props.processingButton]);

    const renderButton = useCallback((item) => {
        const isProcessing = processingName && item.name === processingName;
        const showText = item.showText !== 'inMenu' && item.options?.text;
        const waitingIdcClass = showText ? 'ant-toolbar-icon-with-text' : 'ant-toolbar-icon';
        const classes = ['ant-pos-big-button', ...(item.classNames || [])];

        return (
            <AwesomeButton
                id={`ant-toolbar-button-id-${item.name}`}
                type="primary"
                className={classes.join(' ')}
                onPress={() => onButtonClick(item.name)}
            >
                <LoadIndicator className={waitingIdcClass} visible={isProcessing} />
                <i className={`ant-pos-big-button-icon dx-icon dx-icon-${item.options?.icon || item.icon}`}
                    style={{ marginRight: showText ? 9 : 0, display: isProcessing ? 'none' : 'inline-block', fontSize: '16px' }} />
                <span id={`ant-toolbar-button-text-id-${item.name}`} className="ant-pos-big-button-text dx-button-text"
                    style={{ display: !showText ? 'none' : 'inline-block' }}>
                    {item.options?.text || item.text || ''}
                </span>
            </AwesomeButton>
        );
    }, [processingName, onButtonClick]);

    return (
        <React.Fragment>
            {shouldRender && (
                <div style={{ width: '100%', marginTop: '20px' }}>
                    <Toolbar
                        className="ant-pos-toolbar ant-pos-bottom-toolbar ant-toolbar"
                        disabled={Boolean(isDisabled)}
                        items={mainItems}
                        multiline={true}
                    >
                        <Template name="renderButton" render={renderButton} />
                    </Toolbar>
                    <AntMessageBox ref={msgBox} />
                </div>
            )}
        </React.Fragment>
    );
});
